import { useState } from 'react';
import Input from '../Input/Input';
import './drivercard.css';
import { DropdownItemSex, IDriverUser, Sex } from '../../types/type';
import DropDownInput from '../DropDownInput/DropDownInput';
import { instance } from '../../api/api';
import { formatDateForInput } from '../../utils/date.utils';
import { formatSnils } from '../../utils/validationUtils';
import { pickFieldErrors, toApiError } from '../../utils/apiError';
import {
  DRIVER_FIELDS,
  DriverErrors,
  DriverField,
  IDriverDataPayload,
  snilsDigits,
  snilsError as snilsErrorText,
  validateDriverData,
} from './driverDataForm';

function DriverCard({ user }: { user: IDriverUser }) {
  const [formattedSnils, setFormattedSnils] = useState<string>(user.snils ? formatSnils(user.snils) : '');
  const [snilsError, setSnilsError] = useState<string>('');

  const [birthDay, setBirthday] = useState<string | null>(user.birthDate ? formatDateForInput(user.birthDate) : null);

  const [sex, setSex] = useState<Sex>(user.sex);
  const [licenseNumber, setLicenseNumber] = useState<string | null>(user.licenseNumber ?? null);
  const [licenseSer, setLicenseSer] = useState<string | null>(user.licenseSer ?? null);
  const [liciensDate, setLiciensDate] = useState<string | null>(
    user.licenseIssueDate ? formatDateForInput(user.licenseIssueDate) : null,
  );
  const [licenseExpireDate, setLicenseExpireDate] = useState<string | null>(
    user.licenseExpireDate ? formatDateForInput(user.licenseExpireDate) : null,
  );
  const [editable, setEditable] = useState<boolean>(false);
  const [errors, setErrors] = useState<DriverErrors>({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const clearError = (field: DriverField) => setErrors((prev) => ({ ...prev, [field]: undefined }));

  const dropdownSex: DropdownItemSex[] = [
    { text: 'Мужской', value: 'male' },
    { text: 'Женский', value: 'female' },
  ];
  const handleSelectSex = (value: Sex) => {
    setSex(value);
    clearError('sex');
  };

  const handleSnilsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = snilsDigits(e.target.value);
    if (digits.length > 11) return;

    clearError('snils');
    setFormattedSnils(formatSnils(digits));
    // Пока поле пустое, «обязательное» не показываем: это проверка при сохранении
    setSnilsError(digits ? snilsErrorText(digits) : '');
  };

  const updateDriverCard = (payload: IDriverDataPayload) => instance.put(`/drivers/${user.id}`, payload);

  const handleSave = async () => {
    setSaveError('');
    const { payload, errors: formErrors } = validateDriverData({
      snils: formattedSnils,
      birthDate: birthDay,
      sex,
      licenseSer,
      licenseNumber,
      licenseIssueDate: liciensDate,
      licenseExpireDate,
    });
    setErrors(formErrors ?? {});
    if (!payload) return;

    setSaving(true);
    try {
      await updateDriverCard(payload);
      setEditable(false);
    } catch (err) {
      // Остаёмся в режиме редактирования: ошибки полей под полями, остальное
      // (например, 409 registration_required) — текстом сервера над формой
      const apiError = toApiError(err, 'Не удалось сохранить данные водителя');
      const fieldErrors = pickFieldErrors(apiError, DRIVER_FIELDS);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
      setSaveError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="driver__card">
      {/* <AvatarImageCard user={user} /> */}
      <div className="driver__card-header">
        <div className="driver__card-header__title">Данные водителя</div>
        <div
          className="driver__card-header__descr"
          onClick={() => {
            if (saving) return;
            if (editable) {
              handleSave();
            } else {
              setEditable(true);
            }
          }}
        >
          {editable ? 'Сохранить' : 'Изменить'}
        </div>
      </div>
      {saveError && <div className="input__error driver__card-error">{saveError}</div>}
      <div className="driver__card-body">
        <Input
          type="text"
          label="Снилс"
          id="snils"
          placeholder="XXX-XXX-XXX XX"
          value={formattedSnils}
          onChange={handleSnilsChange}
          required
          editable={editable}
          error={errors.snils ?? snilsError}
        />
        <Input
          type="date"
          label="Дата рождения"
          id="birthday"
          placeholder="Дата рождения"
          value={birthDay !== null ? birthDay : ''}
          onChange={(e) => {
            setBirthday(e.target.value || null);
            clearError('birthDate');
          }}
          required
          editable={editable}
          error={errors.birthDate}
        />
        <DropDownInput
          items={dropdownSex as DropdownItemSex[]}
          onSelect={handleSelectSex}
          initialValue={sex}
          editable={editable}
          label="Пол"
          style={{ gridColumn: '1/2' }}
        />
        {errors.sex && <div className="input__error">{errors.sex}</div>}
        <Input
          type="text"
          label="Серия в/у"
          id="licenseSer"
          placeholder="Серия в/у (4 символа)"
          value={licenseSer !== null ? licenseSer : ''}
          onChange={(e) => {
            setLicenseSer(e.target.value || null);
            clearError('licenseSer');
          }}
          required
          editable={editable}
          error={errors.licenseSer}
        />
        <Input
          type="text"
          label="Номер в/у"
          id="licenseNumber"
          placeholder="Номер в/у (6 цифр)"
          value={licenseNumber !== null ? licenseNumber : ''}
          onChange={(e) => {
            setLicenseNumber(e.target.value || null);
            clearError('licenseNumber');
          }}
          required
          editable={editable}
          error={errors.licenseNumber}
        />
        <Input
          type="date"
          label="Дата выдачи в/у"
          id="role"
          placeholder="Дата выдачи в/у"
          value={liciensDate !== null ? liciensDate : ''}
          onChange={(e) => {
            setLiciensDate(e.target.value || null);
            clearError('licenseIssueDate');
          }}
          required
          editable={editable}
          error={errors.licenseIssueDate}
        />
        <Input
          type="date"
          label="Дата окончания действия в/у"
          id="createdAt"
          placeholder="Дата окончания действия в/у"
          value={licenseExpireDate !== null ? licenseExpireDate : ''}
          onChange={(e) => {
            setLicenseExpireDate(e.target.value || null);
            clearError('licenseExpireDate');
          }}
          editable={editable}
          error={errors.licenseExpireDate}
        />
      </div>
    </div>
  );
}

export default DriverCard;
